import React, { useEffect, useState, useCallback } from 'react';
import { listCustomModels, saveCustomModel, deleteCustomModel, verifyCustomModel, getBucketVerifyToken } from '../firebase/customModels';

const RUNTIME_SA = process.env.REACT_APP_RUNTIME_SA_EMAIL || 'crimsonscan-runtime@ai-discovery-engine.iam.gserviceaccount.com';
const blank = { name: '', family: 'mattergen', mode: 'weights', conditioning: 'unconditional', gcsUri: '', endpointUrl: '' };

export default function CustomModelsPanel({ orgId }) {
  const [models, setModels] = useState([]);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [msg, setMsg] = useState('');
  const [markerPrefix, setMarkerPrefix] = useState('');

  const load = useCallback(async () => {
    if (!orgId) return;
    try { setModels(await listCustomModels(orgId)); } catch (e) { setMsg(e.message || 'load failed'); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setMarkerPrefix('');
    if (!orgId) return;
    getBucketVerifyToken(orgId).then(r => { if (r && r.markerPrefix) setMarkerPrefix(r.markerPrefix); }).catch(() => {});
  }, [orgId]);

  const onAdd = async () => {
    setMsg('');
    if (!form.name.trim()) { setMsg('Name is required.'); return; }
    if (form.mode === 'weights' && !/^gs:\/\//.test(form.gcsUri.trim())) { setMsg('Weights mode needs a gs:// URI.'); return; }
    if (form.mode === 'endpoint' && !/^https:\/\//.test(form.endpointUrl.trim())) { setMsg('Endpoint mode needs an https:// URL.'); return; }
    setBusy(true);
    try {
      const model = {
        name: form.name.trim(), mode: form.mode, family: form.family,
        conditioning: form.family === 'mattergen' ? (form.conditioning.trim() || 'unconditional') : 'unconditional',
        status: 'registered',
        ...(form.mode === 'weights'
          ? { gcsUri: form.gcsUri.trim(), endpointUrl: null, auth: null }
          : { endpointUrl: form.endpointUrl.trim(), gcsUri: null, auth: { type: 'gcp_oidc' } }),
      };
      await saveCustomModel(orgId, model);
      setForm(blank); await load();
    } catch (e) { setMsg(e.message || 'save failed'); }
    finally { setBusy(false); }
  };

  const onVerify = async (id) => {
    setVerifying(id); setMsg('');
    try { const r = await verifyCustomModel(orgId, id); if (!r.ok) setMsg(r.error || 'verification failed'); await load(); }
    catch (e) { setMsg(e.message || 'verify failed'); }
    finally { setVerifying(null); }
  };

  const onDelete = async (id) => { await deleteCustomModel(orgId, id); await load(); };

  const label = { color: '#94a3b8', fontSize: 12, marginBottom: 4 };
  const input = { width: '100%', background: '#0a1120', border: '1px solid #1e3a5f', borderRadius: 6, color: '#e2e8f0', padding: '6px 10px', fontSize: 13 };
  const statusColor = (s) => s === 'verified' ? '#4ade80' : s === 'error' ? '#f87171' : '#94a3b8';

  return (
    <div style={{ background: '#0f1923', borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
      <p style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Custom Models</p>

      {models.length === 0
        ? <p style={{ color: '#334155', fontSize: 13 }}>No custom models yet.</p>
        : models.map((m) => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #16233a' }}>
            <span style={{ color: '#e2e8f0', fontSize: 13, flex: 1 }}>{m.name} <span style={{ color: '#64748b' }}>· {m.family} · {m.mode}</span></span>
            <span style={{ color: statusColor(m.status), fontSize: 12 }}>{m.status}</span>
            <button disabled={verifying === m.id} onClick={() => onVerify(m.id)} style={{ ...input, width: 'auto', cursor: 'pointer' }}>{verifying === m.id ? 'Verifying…' : 'Verify'}</button>
            <button onClick={() => onDelete(m.id)} style={{ ...input, width: 'auto', color: '#f87171', cursor: 'pointer' }}>Delete</button>
          </div>
        ))}

      <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
        <div><p style={label}>Name</p><input style={input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Acme fine-tuned band-gap" /></div>
        <div>
          <p style={label}>Family</p>
          <select style={input} value={form.family} onChange={e => setForm(f => ({ ...f, family: e.target.value, conditioning: e.target.value === 'mattergen' ? f.conditioning : 'unconditional' }))}>
            <option value="mattergen">mattergen — inorganic crystal generation</option>
            <option value="fm4m">fm4m — polymer / small-molecule generation (IBM FM4M)</option>
            <option value="gt4sd">gt4sd — small-molecule generation (GT4SD)</option>
          </select>
        </div>
        <div>
          <p style={label}>Mode</p>
          <select style={input} value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}>
            <option value="weights">weights — we host your checkpoint</option>
            <option value="endpoint">endpoint — you host, we route</option>
          </select>
        </div>
        {form.mode === 'weights' ? (
          <div>
            <p style={label}>Checkpoint GCS URI</p>
            <input style={input} value={form.gcsUri} onChange={e => setForm(f => ({ ...f, gcsUri: e.target.value }))} placeholder="gs://your-bucket/path/to/checkpoint" />
            <p style={{ color: '#38bdf8', fontSize: 11, marginTop: 4 }}>Grant <code>{RUNTIME_SA}</code> the <code>storage.objectViewer</code> role on the bucket.</p>
            <p style={{ color: '#38bdf8', fontSize: 11, marginTop: 4 }}>
              Then prove you own the bucket: create an empty object at <code>gs://&lt;your-bucket&gt;/{markerPrefix || '.crimsonscan-verify/<loading…>'}</code>
            </p>
          </div>
        ) : (
          <div>
            <p style={label}>Endpoint URL (Cloud Run)</p>
            <input style={input} value={form.endpointUrl} onChange={e => setForm(f => ({ ...f, endpointUrl: e.target.value }))} placeholder="https://your-model-xxxxx.run.app" />
            <p style={{ color: '#38bdf8', fontSize: 11, marginTop: 4 }}>Grant <code>{RUNTIME_SA}</code> the <code>run.invoker</code> role on the service.</p>
          </div>
        )}
        {form.family === 'mattergen' && (
          <div>
            <p style={label}>Conditioning</p>
            <select style={input} value={form.conditioning} onChange={e => setForm(f => ({ ...f, conditioning: e.target.value }))}>
              <option value="unconditional">unconditional</option>
              <option value="band_gap">band_gap</option>
              <option value="bulk_modulus">bulk_modulus</option>
              <option value="energy_above_hull">energy_above_hull</option>
              <option value="chemical_system">chemical_system</option>
              <option value="magnetic_density">magnetic_density</option>
              <option value="space_group">space_group</option>
            </select>
          </div>
        )}
        <button disabled={busy} onClick={onAdd} style={{ ...input, cursor: 'pointer', color: '#4ade80', width: 'auto', justifySelf: 'start' }}>{busy ? 'Saving…' : 'Add model'}</button>
      </div>

      {msg && <p style={{ color: '#f87171', fontSize: 12, marginTop: 10 }}>{msg}</p>}
    </div>
  );
}
